import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Package, Truck, Clock, CheckCircle, MapPin, Star, Loader2, Search, ChevronDown, ChevronUp } from 'lucide-react';
import Footer from '../components/Footer';
import Header from '../components/Header';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  pagado: { label: 'Pagado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  paid: { label: 'Pagado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  procesando: { label: 'Preparando', color: 'bg-neutral-100 text-purple-800', icon: Package },
  preparando: { label: 'Preparando', color: 'bg-neutral-100 text-purple-800', icon: Package },
  enviado: { label: 'Enviado', color: 'bg-green-100 text-green-800', icon: Truck },
  entregado: { label: 'Entregado', color: 'bg-emerald-100 text-emerald-800', icon: MapPin },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: Clock },
};

const CustomerDashboardPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim() || !orderNumber.trim()) return;
    setLoading(true);
    setSearched(true);
    setLookupError('');

    try {
      const ordersRes = await axios.post(`${API}/orders/lookup`, { email: email.trim(), order_number: orderNumber.trim() });
      setOrders(ordersRes.data);
    } catch (err) {
      setOrders([]);
      setLookupError(err.response?.data?.detail || 'No se pudieron cargar tus pedidos');
    }

    try {
      // Fetch loyalty
      const loyaltyRes = await axios.get(`${API}/loyalty/member/${email.trim()}`);
      setLoyalty(loyaltyRes.data);
    } catch {
      setLoyalty(null);
    }

    setLoading(false);
  };

  const getStatusInfo = (status) => STATUS_MAP[status] || STATUS_MAP.pendiente;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-pink-50 flex flex-col">
      <Header />
      <div className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <Button onClick={() => navigate('/')} variant="ghost" className="mb-6" data-testid="dashboard-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver al inicio
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black mb-4">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" data-testid="dashboard-title">Mi Cuenta</h1>
          <p className="text-base text-gray-600 mt-2">Consulta tus pedidos y estado de envío</p>
        </div>

        {/* Search Form */}
        <Card className="p-6 mb-8 max-w-md mx-auto" data-testid="dashboard-search-card">
          <form onSubmit={handleSearch} className="space-y-4">
            <Input
              type="email"
              placeholder="Tu email de compra"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="dashboard-email-input"
            />
            <Input
              placeholder="Nº de pedido (ej. JRF-20261027-AB12CD34)"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              required
              data-testid="dashboard-order-number-input"
            />
            <p className="text-xs text-gray-500">Lo encontrarás en el email de confirmación de tu compra. Verás todos tus pedidos.</p>
            <Button type="submit" disabled={loading} className="w-full bg-black hover:bg-neutral-800" data-testid="dashboard-search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              {loading ? 'Buscando...' : 'Ver mis pedidos'}
            </Button>
          </form>
        </Card>

        {searched && !loading && (
          <>
            {/* Loyalty Summary */}
            {loyalty && (
              <Card className="p-5 mb-6 border-2 border-neutral-200 bg-neutral-50/50" data-testid="dashboard-loyalty-summary">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <Star className="w-8 h-8 text-yellow-500" />
                    <div>
                      <p className="text-sm text-gray-500">Programa de Fidelidad</p>
                      <p className="font-bold text-gray-900">Nivel {loyalty.tier_info?.label} — {loyalty.available_points} puntos</p>
                    </div>
                  </div>
                  <Button onClick={() => navigate('/fidelidad')} variant="outline" size="sm" data-testid="dashboard-loyalty-link">
                    Ver detalles
                  </Button>
                </div>
              </Card>
            )}

            {/* Orders */}
            <h2 className="text-xl font-bold text-gray-800 mb-4" data-testid="dashboard-orders-title">
              {orders.length > 0 ? `Mis pedidos (${orders.length})` : 'No se encontraron pedidos'}
            </h2>
            {lookupError && (
              <p className="text-sm text-red-600 mb-4" data-testid="dashboard-lookup-error">{lookupError}</p>
            )}

            {orders.length === 0 && (
              <Card className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No hay pedidos con este email</p>
                <Button onClick={() => navigate('/')} variant="outline" className="mt-4">Ir a comprar</Button>
              </Card>
            )}

            <div className="space-y-4">
              {orders.map(order => {
                const status = getStatusInfo(order.status);
                const StatusIcon = status.icon;
                const isExpanded = expandedOrder === order.id;

                return (
                  <Card key={order.id} className="overflow-hidden" data-testid={`order-card-${order.order_number}`}>
                    {/* Order Header */}
                    <div
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <StatusIcon className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{order.order_number}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(order.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${status.color}`}>
                            {status.label}
                          </span>
                          <span className="font-bold text-gray-900">{order.total?.toFixed(2)}€</span>
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                        </div>
                      </div>
                    </div>

                    {order.tracking_number && (
                      <div className="mx-4 mb-4 p-3 bg-black text-white flex items-center justify-between gap-3 flex-wrap" data-testid={`order-tracking-${order.order_number}`}>
                        <div className="flex items-center gap-2 text-sm">
                          <Truck className="w-4 h-4" />
                          <span className="text-white/70">{order.tracking_carrier || order.shipping_carrier?.carrier || 'Envío'} ·</span>
                          <span className="font-mono" data-testid={`order-tracking-number-${order.order_number}`}>{order.tracking_number}</span>
                        </div>
                        {order.tracking_url && (
                          <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-[11px] font-bold uppercase tracking-[0.2em] bg-white text-black px-4 py-2 hover:bg-neutral-200 transition-colors" data-testid={`order-tracking-link-${order.order_number}`}>
                            Seguir mi envío
                          </a>
                        )}
                      </div>
                    )}

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t px-4 pb-4 pt-3 bg-gray-50/50">
                        {/* Items */}
                        <div className="space-y-2 mb-4">
                          {order.items?.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-gray-700">
                                {item.name} <span className="text-gray-400">— {item.size}/{item.color} x{item.quantity}</span>
                              </span>
                              <span className="font-medium">{(item.price * item.quantity).toFixed(2)}€</span>
                            </div>
                          ))}
                        </div>

                        {/* Totals */}
                        <div className="border-t pt-3 space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Envío</span>
                            <span>{order.shipping_cost > 0 ? `${order.shipping_cost?.toFixed(2)}€` : 'Gratis'}</span>
                          </div>
                          <div className="flex justify-between font-bold">
                            <span>Total</span>
                            <span>{order.total?.toFixed(2)}€</span>
                          </div>
                        </div>

                        {/* Shipping */}
                        {order.shipping_address && (
                          <div className="mt-3 text-xs text-gray-500">
                            <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {order.shipping_address.address}, {order.shipping_address.city} {order.shipping_address.postal_code}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CustomerDashboardPage;
